import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  EVClickAwayListener,
  EVPopper,
  EVList,
  EVListItem,
  EVListItemText,
  EVPaper,
  EVListItemIcon,
  EVSignOut,
  EVTypography,
  EVSettings,
} from '@eagleview/ev-comp-library';
import { useDispatch } from 'react-redux';
import { signOutUser, signOutGuestToSignIn, wait } from 'utils/app.utils';
import { WAIT_AFTER_REVOKE_TOKEN_API_CALL } from 'constants/AppConstants';
import { setIsShareToken } from 'features/authorize/Authorize.actions';
import * as notificationAction from 'features/NotificationsPopup/Notifications.action';
import useStyles from '../UserProfileStyle';

/**
 * A menu component showing user settings
 */
const UserMenu = (props) => {
  const {
    monogramVariant, handleMenuClickAway, open, anchorEl, setViewProfile, userDetails, isGuestUser,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { firstName, lastName } = userDetails;

  const revokeAccessToken = () => dispatch(notificationAction.revokeAccessToken());

  /**
   * Open user profile panel.
   */
  const handleClickOpen = () => {
    setViewProfile(true);
    handleMenuClickAway();
  };

  /**
   * Sign out user. For guest: revoke token then redirect to /signin?redirect=... #genai
   */
  const handleSignOut = async () => {
    handleMenuClickAway();
    if (isGuestUser) {
      revokeAccessToken();
      await wait(WAIT_AFTER_REVOKE_TOKEN_API_CALL);
      const currentResource = window.location.pathname + window.location.search;
      signOutGuestToSignIn(currentResource);
      return;
    }
    revokeAccessToken();
    await wait(WAIT_AFTER_REVOKE_TOKEN_API_CALL);
    dispatch(setIsShareToken(false));
    signOutUser();
  };

  const raw = userDetails.email || userDetails.userName || '--';
  let displayName;
  if (isGuestUser) {
    displayName = raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : '--';
  } else {
    displayName = `${firstName} ${lastName}`;
  }

  return (
    <EVPopper
      id="userContextMenu"
      className={classes.popper}
      placement={monogramVariant === 'medium' ? 'bottom-start' : 'bottom'}
      anchorEl={anchorEl}
      open={open}
    >
      <EVClickAwayListener onClickAway={handleMenuClickAway}>
        <EVPaper
          className={clsx({ [classes.settingsOnSide]: monogramVariant === 'small' },
            { [classes.settingsBelow]: monogramVariant === 'medium' },
            classes.userMenu)}
          elevation={6}
        >
          <EVTypography id="fullName" className={classes.name}>
            {displayName}
          </EVTypography>
          <EVList disablePadding>
            {!isGuestUser && (
              <EVListItem id="userSettings" onClick={() => handleClickOpen()} className={classes.listStyle}>
                <EVListItemIcon className={classes.listItemIcon}>
                  <EVSettings className={classes.signOutIcon} />
                </EVListItemIcon>
                <EVListItemText primary={t('UserSettings')} classes={{ primary: classes.listText }} />
              </EVListItem>
            )}
            <EVListItem id="signOut" className={classes.listStyle} onClick={handleSignOut}>
              <EVListItemIcon className={classes.listItemIcon}>
                <EVSignOut className={classes.signOutIcon} />
              </EVListItemIcon>
              <EVListItemText primary={t('SignOut')} classes={{ primary: classes.listText }} />
            </EVListItem>
          </EVList>
        </EVPaper>
      </EVClickAwayListener>
    </EVPopper>
  );
};

UserMenu.defaultProps = {
  anchorEl: null,
  setViewProfile: () => {},
};

UserMenu.propTypes = {
  /**
   * Variant of monogram
   * @property {string} monogramVariant
   */
  monogramVariant: PropTypes.oneOf(['micro', 'small', 'medium']).isRequired,

  /**
   * Function to be called on clicking outside the menu
   * @property {string} handleMenuClickAway
   */
  handleMenuClickAway: PropTypes.func.isRequired,

  /**
   * Toggles opening and closing of the menu
   * @property {bool} open
   */
  open: PropTypes.bool.isRequired,

  /**
   * Reference object for EVPopper
   * @property {element/object/func} anchorEl
   */
  anchorEl: PropTypes.oneOfType([PropTypes.element, PropTypes.object, PropTypes.func]),

  /**
   * function to show the view profile panel
   * @property {function} setViewProfile
   */
  setViewProfile: PropTypes.func,

  /**
   * Details of the logged in user
   * @property {Object} userDetails
   */
  userDetails: PropTypes.shape().isRequired,

  /**
   * Guest user: hide User Settings, revoke token then redirect to /signin?redirect=... #genai
   * @property {Boolean} isGuestUser
   */
  isGuestUser: PropTypes.bool,
};

UserMenu.defaultProps = {
  ...UserMenu.defaultProps,
  isGuestUser: false,
};

export default UserMenu;
